import type { StripeInfoRecord } from '@/api/custom';

export const TIER_3_STRIPE_CUSTOMER_SENTINEL = 'cus_SENTINEL_TIER_3';

export type BillingStripeInfo =
  | Partial<
      Pick<
        StripeInfoRecord,
        'customer_id' | 'active' | 'subscription_id' | 'subscription_status' | 'no_subscription'
      >
    >
  | null
  | undefined;

export const getStripeCustomerId = (stripeInfo: BillingStripeInfo): string | null => {
  const customerId = stripeInfo?.customer_id;
  if (typeof customerId !== 'string') {
    return null;
  }

  const trimmedCustomerId = customerId.trim();
  return trimmedCustomerId ? trimmedCustomerId : null;
};

export const hasActiveBilling = (stripeInfo: BillingStripeInfo): boolean =>
  getStripeCustomerId(stripeInfo) !== null &&
  (isEnterpriseBilling(stripeInfo) ||
    (stripeInfo?.no_subscription !== true &&
      (stripeInfo?.subscription_status == null
        ? stripeInfo?.active === true
        : ['active', 'trialing', 'past_due'].includes(stripeInfo.subscription_status))));

export const isEnterpriseBilling = (stripeInfo: BillingStripeInfo): boolean =>
  getStripeCustomerId(stripeInfo) === TIER_3_STRIPE_CUSTOMER_SENTINEL;

export const hasStripeBillingPortal = (stripeInfo: BillingStripeInfo): boolean =>
  !isEnterpriseBilling(stripeInfo) &&
  getStripeCustomerId(stripeInfo) !== null &&
  ['active', 'trialing', 'past_due', 'unpaid', 'incomplete'].includes(
    stripeInfo?.subscription_status ?? '',
  );

export const requiresBillingSetup = (stripeInfo: BillingStripeInfo): boolean =>
  !hasActiveBilling(stripeInfo) && !isEnterpriseBilling(stripeInfo);

export const isFirstBillingSetup = (stripeInfo: BillingStripeInfo): boolean =>
  !isEnterpriseBilling(stripeInfo) &&
  !stripeInfo?.subscription_id &&
  !stripeInfo?.subscription_status &&
  !stripeInfo?.no_subscription &&
  stripeInfo?.active !== true;

export const requiresTaxAddress = (stripeInfo: BillingStripeInfo): boolean =>
  getStripeCustomerId(stripeInfo) === null;

export const userCanManageBilling = (user: unknown): boolean => {
  if (typeof user !== 'object' || user === null) {
    return false;
  }

  const role = (user as { role?: unknown }).role;
  return typeof role === 'string' && role.trim().toLowerCase() === 'admin';
};

export const shouldRedirectToBillingSetup = (
  user: unknown,
  stripeInfo: BillingStripeInfo,
): boolean => userCanManageBilling(user) && requiresBillingSetup(stripeInfo);
